import * as amplitude from "@amplitude/analytics-browser";
import { ManagedUserRoles } from "@redotech/account-common/roles";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ReloadTeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { useWalkthrough } from "@redotech/redo-merchant-app-common/walkthrough/use-walkthrough";
import { toast } from "@redotech/redo-web/alert";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { SetupGuideContext } from "../../../setup-guide/provider/setup-guide-context";
import { shouldShowJobLevelBackfillPrompt } from "../job-level-backfill/eligibility";
import {
  RECLAIM_INSTALL_APP_MODAL_ACTIVE_BODY_ATTRIBUTE,
  RECLAIM_INSTALL_APP_MODAL_EVENTS,
  RECLAIM_SHOPIFY_APP_LISTING_URL,
} from "./constants";
import { deferReclaimInstallAppPrompt } from "./defer";
import { shouldShowReclaimInstallAppPrompt } from "./eligibility";
import { shouldSuppressReclaimInstallAppPrompt } from "./prompt-host-visibility";
import { ReclaimInstallAppModal } from "./reclaim-install-app-modal";

const SETUP_TASK_PARAM = "setupTask";

export const ReclaimInstallAppPromptHost = memo(
  function ReclaimInstallAppPromptHost() {
    const team = useContext(TeamContext);
    const user = useContext(UserContext);
    const rpcClient = useContext(RedoMerchantRpcClientContext);
    const reloadTeam = useContext(ReloadTeamContext);
    const setupGuide = useContext(SetupGuideContext);
    const walkthrough = useWalkthrough();
    const location = useLocation();
    const loggedShownTeamId = useRef<string | null>(null);
    const [deferredTeamId, setDeferredTeamId] = useState<string | null>(null);

    const activeSetupTaskId = useMemo(
      () => new URLSearchParams(location.search).get(SETUP_TASK_PARAM),
      [location.search],
    );
    const suppressForSetup = shouldSuppressReclaimInstallAppPrompt({
      activeSetupTaskId,
      setupGuideLoading: setupGuide?.loading,
      showSetupGuideIntake: setupGuide?.showIntake,
      walkthroughActive: walkthrough?.active,
    });
    const eligible = shouldShowReclaimInstallAppPrompt({
      currentUserEmail: user?.email,
      team,
    });
    const isAdmin =
      user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ?? false;
    // The job/level backfill prompt is a higher-priority required blocker; yield
    // to it so the two exclusive-layer modals never mutually hide each other.
    const backfillTakesPriority = shouldShowJobLevelBackfillPrompt({
      user,
      pathname: location.pathname,
    });
    const visible =
      !!team &&
      !!user &&
      !!rpcClient &&
      isAdmin &&
      eligible &&
      !suppressForSetup &&
      !backfillTakesPriority &&
      deferredTeamId !== team._id;

    useReclaimInstallAppModalExclusiveLayer(visible);

    useEffect(() => {
      if (!visible || !team || loggedShownTeamId.current === team._id) {
        return;
      }
      amplitude.logEvent(RECLAIM_INSTALL_APP_MODAL_EVENTS.shown, {
        teamId: team._id,
      });
      loggedShownTeamId.current = team._id;
    }, [team, visible]);

    const [deferLoad, deferPrompt] = useTriggerLoad(async () => {
      if (!team || !rpcClient) {
        return false;
      }
      try {
        await deferReclaimInstallAppPrompt({ rpcClient, teamId: team._id });
        setDeferredTeamId(team._id);
        amplitude.logEvent(RECLAIM_INSTALL_APP_MODAL_EVENTS.deferred, {
          teamId: team._id,
        });
        reloadTeam?.();
        toast("We'll remind you tomorrow.", { variant: "success" });
        return true;
      } catch {
        toast("Could not defer the Reclaim app reminder. Please try again.", {
          variant: "error",
        });
        return false;
      }
    });

    const handleDefer = useHandler(() => {
      void deferPrompt();
    });

    const handleOpenListing = useHandler(() => {
      if (!team) {
        return;
      }
      amplitude.logEvent(RECLAIM_INSTALL_APP_MODAL_EVENTS.openListingClicked, {
        teamId: team._id,
      });
      window.location.assign(RECLAIM_SHOPIFY_APP_LISTING_URL);
    });

    if (!visible) {
      return null;
    }

    return (
      <ReclaimInstallAppModal
        deferPending={deferLoad.pending}
        onDefer={handleDefer}
        onOpenListing={handleOpenListing}
      />
    );
  },
);

function useReclaimInstallAppModalExclusiveLayer(visible: boolean) {
  useLayoutEffect(() => {
    if (!visible) {
      return;
    }

    document.body.setAttribute(
      RECLAIM_INSTALL_APP_MODAL_ACTIVE_BODY_ATTRIBUTE,
      "true",
    );

    return () => {
      if (
        document.body.getAttribute(
          RECLAIM_INSTALL_APP_MODAL_ACTIVE_BODY_ATTRIBUTE,
        ) === "true"
      ) {
        document.body.removeAttribute(
          RECLAIM_INSTALL_APP_MODAL_ACTIVE_BODY_ATTRIBUTE,
        );
      }
    };
  }, [visible]);
}
